<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<template #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<template #content>
			<!-- Figma 7415:16050: the title 24 under the logo row, Continue in the page 24 under the list (not
			     the footer, which the DS pins to the bottom). The option cards are the DS Select's as it draws them. -->
			<LayoutFlex padding="var(--size-24) var(--size-20)" gap="var(--size-24)">
				<LayoutFlex padding="0" gap="var(--size-12)" align-items="flex-start">
					<Text font="--heading-h3" color="--text-dark-primary">
						Before you make changes to your plan, let us know what’s not working
					</Text>
					<Text font="--text-16-20-med" :color="KOVA.textSecondary">Please select all that apply</Text>
				</LayoutFlex>

				<Select type="multiple" :value="selected" gap="var(--size-8)" @ds:change="onChange">
					<template #options>
						<SelectItem v-for="o in REASONS" :key="o.value" :value="o.value">
							<Text font="--text-16-20-semi" color="--text-dark-primary">{{ o.label }}</Text>
							<SelectIndicator />
						</SelectItem>
					</template>
				</Select>

				<Button full-width :disabled="!geo.isOneClickCancelRequired && !selected.length" @ds:click="next">
					<Text font="--text-16-18-semi">Continue</Text>
				</Button>
			</LayoutFlex>

			<!-- "Please tell us more" (Figma 7760:15265): Continue with "Other" picked opens it, Done goes on.
			     The DS Modal's dialog is left transparent and the card is built in the slot, as for
			     "Before you go…" (docs/architecture/styles.md → Modals). The DS has no Escape, so the card does. -->
			<Modal :visible="otherOpen" animation="fade" background="transparent" @ds:close="closeOther">
				<div class="reasons__popup" @keydown.esc="closeOther">
					<LayoutFlex padding="0" gap="var(--size-24)">
						<LayoutFlex padding="0" gap="var(--size-12)" align-items="flex-start">
							<Text font="--heading-h3" color="--text-dark-primary">Please tell us more</Text>
							<Text font="--text-16-20-med" :color="KOVA.textSecondary">
								Your feedback can help us make Kova better for all members, current and future.
							</Text>
						</LayoutFlex>
						<textarea
							ref="otherField"
							v-model="otherText"
							class="reasons__field"
							placeholder="e.g. propose a feature you definitely missed"
						/>
						<Button full-width @ds:click="submitOther">
							<Text font="--text-16-18-semi">Done</Text>
						</Button>
					</LayoutFlex>
				</div>
			</Modal>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
	Button,
	LayoutFlex,
	LayoutScreen,
	Modal,
	Select,
	SelectIndicator,
	SelectItem,
	Text,
} from '@simple-life-app/growth-ui-kova'
import CancelHeader from '@/components/CancelHeader.vue'
import type { ExitReason } from '@/api/medBilling'
import { useGeoStore } from '@/store/geo'
import { useSurveyStore } from '@/store/survey'
import { KOVA } from '@/designTokens'
import { STEP_TITLE, useCancelStepLogging } from '@/analytics'
import { usePageScrollLock } from '@/composables/usePageScrollLock'

const router = useRouter()
const survey = useSurveyStore()
const geo = useGeoStore()

// Match the Figma `kova cancel flow2` frame (28386:8261). Order and copy mirror the design.
// The `value`s are the reason codes shared with the Kova admin (DEV-36293, `EXIT_REASONS`): the exit
// calls send the picked ones as `exit_reason` (`survey.exitReasons`), and they double as the
// `data.responses` ids sent to analytics. The copy is the design's, unchanged.
const REASONS: ReadonlyArray<{ value: ExitReason; label: string }> = [
	{ value: 'side_effects', label: 'Side effects from medication' },
	{ value: 'not_seeing_results', label: 'Not seeing expected results' },
	{ value: 'found_other_provider', label: 'Found a different program/provider' },
	{ value: 'prescription_or_shipment', label: 'Problems with prescription or shipment' },
	{ value: 'no_longer_needed', label: 'No longer need a medically-assisted weight loss program' },
	{ value: 'difficulty_administering', label: 'Difficulty administering medication' },
	{ value: 'lack_of_care_support', label: 'Lack of support from clinician, care team, or coach' },
	{ value: 'too_expensive', label: 'Too expensive' },
	{ value: 'other', label: 'Other' },
]

const selected = computed<string[]>(() => {
	const raw = survey.answers['cancellation_reasons']
	return Array.isArray(raw) ? (raw as string[]) : []
})

const step = useCancelStepLogging(STEP_TITLE.WHY_CANCELLING, { getResponses: () => selected.value })

function onChange(next: string | string[]) {
	const values = Array.isArray(next) ? next : [next]
	survey.setAnswer('cancellation_reasons', values)
	step.record(values)
}

function next() {
	if (selected.value.includes('other')) {
		otherOpen.value = true
		nextTick(() => otherField.value?.focus())
		return
	}
	router.push('/cancel/likelihood')
}

// "Other" asks for a few words first (Figma 7760:15265). Billing takes reason codes only, so the text
// goes nowhere but the step's analytics event (`data.text`), as on the feedback screen. It's recorded
// on Done only: closing the popup (Escape, a tap outside) sends nothing; nothing is required to go on.
const otherOpen = ref(false)
// The page scrolls the window (growth-ds ≥ 0.60): the popup holds it still behind its overlay, as on 0.58.
usePageScrollLock(otherOpen)
const otherText = ref('')
const otherField = ref<HTMLTextAreaElement | null>(null)

function closeOther() {
	otherOpen.value = false
}

function submitOther() {
	step.record(selected.value, otherText.value.trim())
	otherOpen.value = false
	router.push('/cancel/likelihood')
}
</script>

<style scoped>
/* 7760:15466: white, radius 36, padding 20; the DS dialog sets the width (16 from the edges). */
.reasons__popup {
	padding: var(--size-20);
	background: var(--background-white);
	border-radius: var(--size-36);
}

/* 7760:15489 — the feedback screen's field: 112 tall, 2px navy-10% border, radius 16. */
.reasons__field {
	width: 100%;
	height: 112px;
	padding: var(--size-12) var(--size-16);
	border: 2px solid rgba(50, 54, 83, 0.1);
	border-radius: var(--size-16);
	background: var(--background-white);
	font: var(--text-16-22-semi);
	color: var(--text-dark-primary);
	resize: none;
	box-sizing: border-box;
}

.reasons__field::placeholder {
	color: rgba(50, 54, 83, 0.4);
}

.reasons__field:focus {
	outline: none;
	border-color: var(--stroke-accent-brand);
}
</style>
